import _keyBy from 'lodash-bound/keyBy'
import _each from 'lodash-bound/each'
import Camelizer from '../utils/Camelizer'
import { GroupType } from './enums'
import addEnsureEntity from './extensions/ensureEntity'

export const CONTACTS_ORG_ID = 'UpM28mnfW65vJkbqEATtKvxm'

function getSettingValue(settings, key) {
  return (key in settings) ? settings[key].value : null
}

export default function(store, { host }) {
  const Organization = store.defineModel('organization', {
    instanceEvents: true,

    replaceKeys: {
      allowMsgForward: '__isForwardAllowed',
      allowPublicGroups: 'forumsEnabled',
      allowRoleScheduleUpload: '__isRolesScheduleUploadEnabled',
      desktopPriorityMessaging: '__desktopPriorityMessagingSounds',
      eula: 'eulaEnabled',
      eulaContent: 'eulaUrl',
      featurePatientCare: '__canShowPatientCare',
      featurePatientNetwork: 'isPatientNetworkEnabled',
      featureRolesEscalation: '__canEscalateMessages',
      featureRoleScheduleUpload: '__canUploadRolesSchedule',
      featureRoleService: 'rolesEnabled',
      featureTransactions: 'canShowTransactions',
      members: 'memberCount',
      patientCare: '__showPatientCare',
      rolesEscalation: '__isRolesEscalationEnabled',
      roleService: 'canAdminRoles',
      ssoWebSessionDuration: 'desktopExtendedSession',
      organizationKey: 'organizationId',
    },

    // Additional unused settings:
    //
    // pin_lock (optional): Default PIN lock setting for organization users
    // settings_restricted (optional): Can organization users change their settings?
    // lifecycle_callback (optional): Rest call to be invoked when adding new user to the organization
    // group_lifespan (optional): The number of minutes a group should live after the last message has been sent, -1 turns off group_lifespan. Affects all existing groups in the org.
    // welcome_email_settings (optional): A JSON object that contains the welcome email settings (with the fields "from", "subject", "optional_header", "optional_message", "optional_name")
    // welcome_email_logo (optional): A base 64 encoding of an image. Can be set only with the application/json format
    // auto_send_welcome_email (optional): A boolean value indicating whether welcome emails get sent automatically on accounts joining orgs or not

    defaultValues: {
      __canEscalateMessages: false,
      __canShowPatientCare: false,
      __canUploadRolesSchedule: false,
      __isForwardAllowed: false,
      __isRolesEscalationEnabled: false,
      __isRolesScheduleUploadEnabled: false,
      __showPatientCare: false,
      _unreadMessages: null,
      _unreadPriorityMessages: null,
      canAdminRoles: false,
      canShowTransactions: false,
      desktopExtendedSession: -1,
      forumsEnabled: false,
      highestSortNumber: 0,
      lastIncomingMessageSortNumber: 0,
      networks: null,
      paid: false,
      reportingV2Enabled: false,
      rolesEnabled: false,
      settings: {},
      unreadCount: 0,
      unreadPriorityCount: 0,
    },

    transientAttrs: [
      'unreadCount',
      'unreadPriorityCount',
    ],

    parseAttrs(attrs) {
      if (attrs.settings) {
        attrs.settings = attrs.settings
          ::_keyBy(e => Camelizer.camelizeKey(e.name))
          ::_each(e => {
            if (isFinite(e.value)) e.value = +e.value
            if (e.value === 'true') e.value = true
            if (e.value === 'false') e.value = false
          })
      }

      return attrs
    },

    afterAssignment(entity) {
      let {
        __canEscalateMessages,
        __canShowPatientCare,
        __canUploadRolesSchedule,
        __desktopPriorityMessagingSounds,
        __isForwardAllowed,
        __isRolesEscalationEnabled,
        __isRolesScheduleUploadEnabled,
        __showPatientCare,
        autoForwardReceiverId,
        id,
        name,
        paid,
        settings,
      } = entity

      autoForwardReceiverId = getSettingValue(settings, 'dndAutoForwardReceiver')
      entity.allowPriorityMessage = !!getSettingValue(settings, 'priorityMessaging')
      entity.autoLogoutMinutes = getSettingValue(settings, 'autologoutTime') || 1440 // 24 hours
      entity.canUploadRolesSchedule = __canUploadRolesSchedule && __isRolesScheduleUploadEnabled
      entity.deleteOnRead = !!getSettingValue(settings, 'dor')
      entity.desktopAutoUpdate = !!getSettingValue(settings, 'autoUpdate')
      entity.isEulaAccepted = !getSettingValue(settings, 'eula') // True for displaying the prompt and false to never display it again
      entity.isForwardAllowed = __isForwardAllowed && getSettingValue(settings, 'forward') === 1
      entity.messageTimeToLive = getSettingValue(settings, 'ttl') || -1

      const isContacts = entity.id === CONTACTS_ORG_ID
      entity.allowedSenders = host.organizations._getAllowedSenders(entity)
      entity.autoForwardReceiverId = autoForwardReceiverId
      entity.autoForwardReceiver = autoForwardReceiverId ? host.models.User.get(autoForwardReceiverId) : null
      entity.canEscalateMessages = __canEscalateMessages && __isRolesEscalationEnabled
      entity.desktopPriorityMessagingSounds = paid && __desktopPriorityMessagingSounds
      entity.displayName = isContacts ? 'Contacts' : name
      entity.isContacts = isContacts
      entity.showPatientCare = __canShowPatientCare && __showPatientCare
      entity.userRoles = host.userRoles.getByOrganizationId(id)
      entity.reportingV2Enabled = host.config.reportingV2Enabled

      if (!entity._unreadMessages) entity._unreadMessages = []
      if (!entity._unreadPriorityMessages) entity._unreadPriorityMessages = []

      if (!entity.networks) entity.networks = {}

      if (!entity.networks.provider) {
        entity.networks.provider = {
          unreadCount: 0,
          unreadPriorityCount: 0,
        }
      }

      if (!entity.networks.patient) {
        entity.networks.patient = {
          unreadCount: 0,
          unreadPriorityCount: 0,
        }
      }
    },

    methods: {
      getSettingValue(key) {
        return getSettingValue(this.settings, key)
      },
      getSettingEntry(key) {
        return this.settings && (key in this.settings) ? this.settings[key] : null
      }
    },

    relations: {
      hasMany: {
        __expirableMessages: {
          type: 'message', foreignKey: '__organizationId',
          filter: message => !!message.expiresAt,
          sortBy: [['expiresAt', 'asc']],
        },
        __expirableMutes: {
          type: 'mute', foreignKey: 'organizationId',
          filter: mute => !!mute.expiresAt,
          sortBy: [['expiresAt', 'asc']],
        },
        __markingAsReadConversations: {
          type: 'conversation', foreignKey: 'organizationId',
          filter: (conversation) => !!conversation._markingAsRead,
          sortBy: [['_markingAsReadExpiration', 'asc']],
        },
        conversations: {
          type: 'conversation', foreignKey: 'organizationId',
          filter: (conversation) => {
            const { counterParty } = conversation
            if (!counterParty) return false

            const { groupType } = counterParty
            if (groupType === GroupType.ROLE_ASSIGNMENT) return false

            return true
          },
          sortBy: [['highestSortNumber', 'desc'], ['_origIndex', 'asc']],
        },
        roles: {
          type: 'role', foreignKey: 'organizationId',
        },
        roleTags: {
          type: 'roleTag', foreignKey: 'organizationId',
        },
      },
    },
  })

  addEnsureEntity(Organization, {
    finder: (...args) => host.organizations.find(...args),
    placeholderEntityDelayBeforeRefreshing() { return host.config.placeholderEntityDelayBeforeRefreshing },
    placeholderEntityAllowLoading() { return host.config.placeholderEntityAllowLoading },

    defaultPlaceholderAttrs: { name: 'Loading Organization...' },
    defaultNotFoundAttrs: { displayName: '<Organization Not Found>' },
  })

  return Organization
}
